import * as React from 'react';
import { Drawer as MuiDrawer, IconButton, Box } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import { useRouter } from 'next/router';

import {
  DoubleArrow,
  ErrorBoundary,
  ErrorBoundaryFallback,
  SideBarTicketCreation,
} from '@components';
import theme from 'config/theme';
import styles from './drawer.style';

const ticketDrawerStyles = {
  drawer: ({ width, open, isResizing }) => ({
    top: theme.customValues.topBarHeight,
    height: `calc(100vh - ${theme.customValues.topBarHeight})`,
    overflowX: 'hidden',
    width: open ? width : 0,
    transition: isResizing ? 'none' : 'width 0.3s',
    '& .MuiDrawer-paper': {
      width: open ? width : 0,
      maxWidth: 'none',
      top: theme.customValues.topBarHeight,
      boxSizing: 'border-box',
      transition: isResizing ? 'none' : 'width 0.3s',
      height: `calc(100vh - ${theme.customValues.topBarHeight})`,
      padding: open ? '20px' : '0',
      paddingLeft: open ? '0px' : '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '44px',
      overflowX: 'hidden',
    },
  }),
  resizeHandle: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '10px',
    cursor: 'col-resize',
    zIndex: 1301,
    '&:hover': {
      borderLeft: '2px solid #3B82F6',
    },
  },
  drawerContent: {
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '44px',
    flex: 1,
    overflow: 'auto',
  },
  toolbar: {
    border: 'none',
    backgroundColor: '#FFFFFF',
    padding: '0 !important',
    paddingLeft: '20px !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '4px',
    minHeight: `30px !important`,
  },
  title: { fontWeight: '700', fontSize: '1.25rem' },
  toggleButton: (open) => {
    if (open) {
      return {
        padding: 0,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s',
        '&:hover': {
          backgroundColor: '#FFFFFF!important',
        },
      };
    }
    return {
      position: 'fixed',
      right: 0,
      top: `calc(${theme.customValues.topBarHeight} + 30px)`,
      zIndex: 1300,
      padding: 0,
      '&:hover': {
        backgroundColor: '#FFFFFF!important',
      },
    };
  },
};

const TicketDrawer = ({ open, onToggle }) => {
  const { pathname } = useRouter();
  const minWidth = parseInt(theme.customValues.ticketCreationDrawerWidth, 10);
  const [drawerWidth, setDrawerWidth] = React.useState(minWidth);
  const [isResizing, setIsResizing] = React.useState(false);
  const resizeStartX = React.useRef(0);
  const startWidth = React.useRef(minWidth);
  const resizeTimeoutRef = React.useRef(null);

  const isServicePointsPage = pathname?.startsWith('/servicePoints');
  const shouldFetchServicePoints = open && !isServicePointsPage;

  const handleMouseDown = React.useCallback(
    (e) => {
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      startWidth.current = drawerWidth;
      e.preventDefault();
    },
    [drawerWidth]
  );

  const handleMouseMove = React.useCallback(
    (e) => {
      if (!isResizing) return;

      const delta = resizeStartX.current - e.clientX;
      const newWidth = Math.max(minWidth, startWidth.current + delta);
      setDrawerWidth(newWidth);

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      window.dispatchEvent(new Event('resize'));

      resizeTimeoutRef.current = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    },
    [isResizing, minWidth]
  );

  const handleMouseUp = React.useCallback(() => {
    setIsResizing(false);
    window.dispatchEvent(new Event('resize'));
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        iframe.style.pointerEvents = 'none';
      });

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        iframes.forEach((iframe) => {
          iframe.style.pointerEvents = '';
        });
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <>
      {!open && (
        <IconButton
          onClick={onToggle}
          sx={ticketDrawerStyles.toggleButton(open)}
        >
          <DoubleArrow />
        </IconButton>
      )}
      <MuiDrawer
        variant="permanent"
        anchor="right"
        open={open}
        sx={{
          ...styles.drawer(`${drawerWidth}px`),
          ...ticketDrawerStyles.drawer({
            width: `${drawerWidth}px`,
            open,
            isResizing,
          }),
        }}
      >
        {open && (
          <Box
            sx={ticketDrawerStyles.resizeHandle}
            onMouseDown={handleMouseDown}
          />
        )}
        <Toolbar
          sx={{
            ...styles.toolbar(`${drawerWidth}px`),
            ...ticketDrawerStyles.toolbar,
          }}
        >
          <IconButton
            onClick={onToggle}
            sx={ticketDrawerStyles.toggleButton(open)}
          >
            <DoubleArrow />
          </IconButton>
          <Box sx={ticketDrawerStyles.title}>Create New Ticket</Box>
        </Toolbar>
        <Box sx={ticketDrawerStyles.drawerContent}>
          <ErrorBoundary
            showErrorDetails={process.env.NODE_ENV === 'development'}
            compact
            fallback={
              <ErrorBoundaryFallback
                title="Ticket Creation drawer crashed .."
                description="Please try refreshing the page."
              />
            }
          >
            <SideBarTicketCreation
              shouldFetchServicePoints={shouldFetchServicePoints}
            />
          </ErrorBoundary>
        </Box>
      </MuiDrawer>
    </>
  );
};

const memoizedTicketDrawer = React.memo(TicketDrawer);
export { memoizedTicketDrawer as TicketDrawer };

export default memoizedTicketDrawer;
